import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ShieldCheck } from 'lucide-react';
import { get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { isValidTotp, TotpField } from './OwnerConfirm';
import { safeReturnTo } from '../opd/BookAppointmentPage';

interface EditableTenant {
  id: string;
  name: string;
  slug: string;
  locale: string;
  customDomain: string | null;
}

export function EditTenantPage() {
  const { tenantId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), `/owner/tenants/${tenantId}`);

  const tenant = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => get<EditableTenant>(`/platform/tenants/${tenantId}`),
    enabled: Boolean(tenantId),
  });

  if (!tenant.data) {
    return (
      <FormScreen title="Edit facility" icon={Building2} backTo={returnTo}>
        <RecordState error={tenant.error} />
      </FormScreen>
    );
  }

  return <EditTenantForm key={tenant.data.id} tenant={tenant.data} returnTo={returnTo} />;
}

function EditTenantForm({ tenant, returnTo }: { tenant: EditableTenant; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [initial] = useState(() => ({
    name: tenant.name,
    locale: tenant.locale,
    customDomain: tenant.customDomain ?? '',
  }));
  const [form, setForm] = useState(initial);
  const [reason, setReason] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      put(`/platform/tenants/${tenant.id}/profile`, {
        totpCode,
        reason: reason.trim(),
        name: form.name.trim(),
        locale: form.locale.trim(),
        customDomain: form.customDomain.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Hospital updated');
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = form.name.trim().length >= 2 && reason.trim().length >= 5 && isValidTotp(totpCode);
  const dirty = !save.isSuccess && (JSON.stringify(form) !== JSON.stringify(initial) || reason !== '' || totpCode !== '');

  return (
    <FormScreen
      title="Edit facility"
      description={tenant.slug}
      icon={Building2}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title="Facility" icon={Building2}>
        <Field label="Name" required className="sm:col-span-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Locale">
          <Input value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })} />
        </Field>
        <Field label="Custom domain" hint="Leave empty to keep using the slug subdomain.">
          <Input value={form.customDomain} placeholder={`${tenant.slug}.example.com`} onChange={(e) => setForm({ ...form, customDomain: e.target.value })} />
        </Field>
        <p className="text-xs text-muted-foreground sm:col-span-2">
          Packages and add-ons are changed from the Modules tab; price and limits from the Account tab. Country and currency belong to each branch and are set
          by the facility when it opens the branch.
        </p>
        <Alert className="sm:col-span-2">The slug never changes — it is baked into stored URLs and every user&apos;s bookmarks.</Alert>
      </FormSection>

      <FormSection title="Confirm" icon={ShieldCheck} columns={1}>
        <Field label="Reason" required hint="Recorded in the platform audit trail — a ticket number is ideal.">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <TotpField value={totpCode} onChange={setTotpCode} />
        {error && <Alert variant="destructive">{error}</Alert>}
      </FormSection>

      <FormActions submitLabel="Save changes" loading={save.isPending} incomplete={!ready} cancelTo={returnTo} bleed={false} />
    </FormScreen>
  );
}
